import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Buttons,
  Chip,
  FlexContainer,
  Icon,
  ModalContext,
  Separator,
  Spinner,
  Text,
} from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useAutoRequestPreviousYear } from 'Containers/PreviousYears/hooks';
import { increasePrice, parsePriceFormat } from 'Containers/Result/components/PaymentLater/utils';
import { useGetPlanFlexInfo } from 'Containers/Result/hooks';
import { useFetchGetPlans } from 'Containers/Plans/hooks';
import { EfirmaInfo } from 'Components/Modal/components';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import { FRONT_PLANS } from 'Constants/plans';
import { routes } from 'Constants/routes';
import { NOT_EFIRMA_KEY } from 'Constants/elements';
import * as Styled from './styles';

const ResultFilter = (): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState<number>(0);
  const paymentLaterProps = useSelector(routeStateSelector);
  const { showModal } = useContext(ModalContext);
  useAutoRequestPreviousYear();
  const { t } = useTranslation(locizeNS.RESULT);
  const { data, isLoading } = useGetPlanFlexInfo();
  const { data: plans, isLoading: isLoadingPlans } = useFetchGetPlans();

  const haveEfirma = selectedOption === 0;
  const notEfirma = selectedOption === 1;

  const planPro = plans?.find(
    plan => plan.name === FRONT_PLANS.PRO || plan.name === FRONT_PLANS.PRO_PLUS
  );

  const handleRedirect = (): void => {
    if (notEfirma) {
      StorageService.setItem(NOT_EFIRMA_KEY, 'true');
      history.push(routes.PAYMENT_LATER, {
        fromFedUser: true,
        ...paymentLaterProps,
      });
    } else {
      StorageService.removeItem(NOT_EFIRMA_KEY);
      history.push(routes.UPSELLING_PRO, {
        fromFedUser: true,
        ...paymentLaterProps,
      });
    }
  };

  const hanldeShowModal = (): void => {
    showModal({
      comp: <EfirmaInfo isPayLater />,
    });
  };

  return (
    <FlowColLayout section="result">
      <FlexContainer direction="column" fullHeight justify="space-between">
        {isLoading || isLoadingPlans ? (
          <Spinner size="M" align="center" justify="center" />
        ) : (
          <>
            <Styled.Container align="center" direction="column" gap="dimension_16">
              <Styled.TitleContainer align="flex-start" direction="column" gap="dimension_16">
                <Text type="headline" color="dark" size="XS_L_M">
                  {t('Result.filter.title')}
                </Text>
                {paymentLaterProps?.havePreviousYears && (
                  <Styled.PreviousYearContainer size="S" mode="success">
                    <FlexContainer justify="space-between" fullWidth>
                      <Text type="paragraphs" color="success_strong" size="S_L_S">
                        {t('Result.toRecover.title')}
                      </Text>
                      <Text type="paragraphs" color="success_strong" size="S_L_S">
                        {`${YayoyakiFormatter.formatAmountMxn(Math.abs(Number(paymentLaterProps?.taxReturnResult)))} MXN`}
                      </Text>
                    </FlexContainer>
                  </Styled.PreviousYearContainer>
                )}
                <Text type="paragraphs" color="grey" size="S_M_L">
                  {t('Result.filter.subtitle')}
                </Text>
              </Styled.TitleContainer>
              <FlexContainer align="center" justify="center" direction="column" gap="dimension_16">
                <Styled.ButtonBox
                  checked={haveEfirma}
                  direction="column"
                  fullHeight
                  gap={haveEfirma ? 'dimension_08' : 'dimension_16'}
                  onClick={() => setSelectedOption(0)}
                >
                  <Styled.Body direction="column" gap="dimension_08">
                    <FlexContainer gap="dimension_08" direction="column" align="flex-start">
                      <FlexContainer fullWidth justify="space-between" align="center">
                        <Text type="paragraphs" color="dark" size="M_XL_S">
                          {t('Result.have.efirma.title')}
                        </Text>
                        <FlexContainer direction="column" align="flex-end">
                          <FlexContainer gap="dimension_04">
                            <Styled.Price
                              type="paragraphs"
                              size="M_S_S"
                              color="danger_strong"
                              align="center"
                            >
                              {increasePrice(
                                Number(parsePriceFormat(planPro?.priceUser?.amount || 0, true))
                              )}
                            </Styled.Price>
                            <Text type="paragraphs" color="dark" size="M_M_S">
                              {parsePriceFormat(Math.abs(planPro?.priceUser?.amount || 0))}
                            </Text>
                          </FlexContainer>
                          <Text type="paragraphs" color="grey" size="XS_M_S">
                            {t('Result.have.efirma.payNow')}
                          </Text>
                        </FlexContainer>
                      </FlexContainer>
                      <Separator />
                      <Text type="paragraphs" color="dark" size="S_L_M">
                        {t('Result.have.efirma.info')}
                      </Text>
                      <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                        {t('Result.have.efirma.first.bullet')}
                      </Styled.BulletParagraph>
                      <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                        {t('Result.have.efirma.second.bullet')}
                      </Styled.BulletParagraph>
                      <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                        {t('Result.have.efirma.third.bullet')}
                      </Styled.BulletParagraph>
                      <Chip type="cold_line" size="S">
                        {t('Result.recommended.title')}
                      </Chip>
                    </FlexContainer>
                    {haveEfirma && (
                      <Styled.Success>
                        <Icon icon="CircleTickSolidXS" color="cold" width={16} height={16} />
                      </Styled.Success>
                    )}
                  </Styled.Body>
                </Styled.ButtonBox>
                <Styled.ButtonBox
                  checked={notEfirma}
                  direction="column"
                  align="center"
                  fullHeight
                  gap={notEfirma ? 'dimension_08' : 'dimension_16'}
                  onClick={() => setSelectedOption(1)}
                >
                  <Styled.Body direction="column" gap="dimension_08">
                    <FlexContainer gap="dimension_08" direction="column" align="flex-start">
                      <FlexContainer fullWidth justify="space-between" align="center">
                        <Text type="paragraphs" color="dark" size="M_XL_S">
                          {t('Result.not.efirma.title')}
                        </Text>
                        <FlexContainer direction="column" align="flex-end">
                          <Text type="paragraphs" color="dark" size="M_M_S">
                            {parsePriceFormat(Math.abs(data?.discountedPrice || 0))}
                          </Text>
                          <Text type="paragraphs" color="grey" size="XS_M_S">
                            {t('Result.not.efirma.payNow')}
                          </Text>
                        </FlexContainer>
                      </FlexContainer>
                      <Separator />
                      <Text type="paragraphs" color="dark" size="S_L_M">
                        {t('Result.not.efirma.info')}
                      </Text>
                      <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                        {t('Result.not.efirma.first.bullet')}
                      </Styled.BulletParagraph>
                      <Styled.BulletParagraph type="paragraphs" color="dark" size="XS_M_M">
                        {t('Result.not.efirma.second.bullet')}
                      </Styled.BulletParagraph>
                    </FlexContainer>
                    {notEfirma && (
                      <Styled.Success>
                        <Icon icon="CircleTickSolidXS" color="cold" width={16} height={16} />
                      </Styled.Success>
                    )}
                  </Styled.Body>
                </Styled.ButtonBox>
              </FlexContainer>
              <Buttons.Tiny label={t('Upselling.tiny.button')} onClick={hanldeShowModal} />
            </Styled.Container>
            <Styled.ButtonContainer align="center" justify="center">
              <Buttons.Classic
                mode="primary"
                label={t('Result.filter.button')}
                size="m"
                onClick={handleRedirect}
              />
            </Styled.ButtonContainer>
          </>
        )}
      </FlexContainer>
    </FlowColLayout>
  );
};

export default ResultFilter;
